/**
 * Order-independent stringify — sorts keys lexicographically at every
 * nesting level before emitting JSON.
 *
 * Necessary because Postgres JSONB normalises object key order on
 * storage (keys are sorted by length, then by binary collation), so a
 * payload loaded from the BE comes back with a different key order
 * than the FE constructed it in. A plain `JSON.stringify` comparison
 * would flag structurally identical objects as different and leave
 * every just-hydrated step's form in a permanently-dirty state.
 *
 * Arrays preserve their order (positional semantics matter, e.g. the
 * UBO entries[] list).
 */
export function stableStringify(value: unknown): string {
    return JSON.stringify(value, (_key, v) => {
        if (v && typeof v === "object" && !Array.isArray(v)) {
            const obj = v as Record<string, unknown>;
            const sorted: Record<string, unknown> = {};
            for (const k of Object.keys(obj).sort()) sorted[k] = obj[k];
            return sorted;
        }
        return v as unknown;
    });
}

/**
 * Shared dirty-check for the Save / Saved / Continue button state in
 * onboarding forms. Matches the expression each form used to hand-roll:
 *
 *   - no baseline yet           → dirty (form has never been saved)
 *   - no current payload yet    → not dirty (form isn't submittable;
 *                                  the button is in its disabled state
 *                                  for an unrelated reason)
 *   - both present              → dirty iff stableStringify differs
 *
 * Use this anywhere a form compares its `currentPayload` to the
 * baseline returned from `submit…()`. See payloadIsDirty.ts header for
 * why a plain JSON.stringify compare is wrong.
 */
export function payloadIsDirty<T>(current: T | null, baseline: T | null): boolean {
    if (baseline === null) return true;
    if (current === null) return false;
    return stableStringify(current) !== stableStringify(baseline);
}
